import type { IncomingMessage } from 'http'
import { getServerSideHttpClient } from 'shared/api/http-client.server'
import { isCustomDomainByHostname } from 'shared/utils/is-custom-domain'
import { buildBaseUrlFromHost, getRequestHostInfo } from 'shared/utils/request-host'
import { BRAND_SETTINGS_API_URL, defaultBrandSettings } from '../constants/brand-settings'
import { BrandSettingsInput, BrandSettingsInterface } from '../types/brand-settings'
import { normalizeBrandSettings } from './normalize-brand-settings'

const REQUEST_TIMEOUT_MS = 1000

interface BrandAwareRequest extends IncomingMessage {
  __brandSettingsCache?: BrandSettingsInterface | null
  __brandSettingsResolved?: boolean
}

export const loadBrandSettingsServerSide = async (
  request?: IncomingMessage | null,
): Promise<BrandSettingsInterface | null> => {
  const req = request as BrandAwareRequest | undefined

  if (!req) {
    return defaultBrandSettings
  }

  if (req.__brandSettingsResolved) {
    return req.__brandSettingsCache ?? defaultBrandSettings
  }

  const { host, forwardedProtocol } = getRequestHostInfo(req)

  if (!host || !isCustomDomainByHostname(host)) {
    req.__brandSettingsResolved = true
    req.__brandSettingsCache = defaultBrandSettings
    return req.__brandSettingsCache
  }

  const baseUrl = buildBaseUrlFromHost(host, forwardedProtocol)

  if (!baseUrl) {
    req.__brandSettingsResolved = true
    req.__brandSettingsCache = defaultBrandSettings
    return req.__brandSettingsCache
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const httpClient = getServerSideHttpClient(baseUrl, process.env.APP_ENV !== 'prod')

    const response = await httpClient.get<BrandSettingsInput | null>(BRAND_SETTINGS_API_URL, {
      signal: controller.signal,
      headers: req.headers.cookie ? { Cookie: req.headers.cookie } : undefined,
    })

    const resolvedBrand = normalizeBrandSettings(
      response.status >= 200 && response.status < 300 ? response.data : null,
      defaultBrandSettings,
    )

    req.__brandSettingsCache = resolvedBrand
    req.__brandSettingsResolved = true
    return resolvedBrand
  } catch {
    req.__brandSettingsResolved = true
    req.__brandSettingsCache = defaultBrandSettings
    return defaultBrandSettings
  } finally {
    clearTimeout(timeoutId)
  }
}
